/**
 * Liquidation Risk Indicator Component
 * Shows user's current liquidation risk with visual indicator and recommendations
 */

import { ArrowUpIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '../../assets/icons'

interface LiquidationRiskIndicatorProps {
  /** Total collateral value in USD */
  collateralValueUSD: number
  /** Total LOC value in USD */
  locValueUSD: number
  /** Compact mode for smaller displays */
  compact?: boolean
  /** Show detailed recommendations */
  showRecommendations?: boolean
  /** Optional custom title */
  title?: string
}

export function LiquidationRiskIndicator({
  collateralValueUSD,
  locValueUSD,
  compact = false,
  showRecommendations = true,
  title = 'Liquidation Risk',
}: LiquidationRiskIndicatorProps) {
  // Calculate Loan-to-Value ratio
  const ltv = collateralValueUSD > 0 ? (locValueUSD / collateralValueUSD) * 100 : 0
  
  // Determine risk level
  const isCaution = ltv > 50 && ltv <= 75 // Yellow - OK
  const isDanger = ltv > 75 // Red - Danger
  
  // Risk level details
  const riskLevel = isDanger ? 'High Risk' : isCaution ? 'Medium Risk' : 'Low Risk'
  const riskColor = isDanger 
    ? 'text-red-600 dark:text-red-400' 
    : isCaution 
    ? 'text-yellow-600 dark:text-yellow-400' 
    : 'text-green-600 dark:text-green-400'
  const riskBg = isDanger 
    ? 'bg-red-50 dark:bg-red-900/20 border-red-500' 
    : isCaution 
    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500' 
    : 'bg-green-50 dark:bg-green-900/20 border-green-500'
  const barColor = isDanger 
    ? 'bg-red-500' 
    : isCaution 
    ? 'bg-yellow-500' 
    : 'bg-green-500'
  
  // Calculate safe threshold positions
  const cautionThreshold = 50
  const dangerThreshold = 75
  // liquidation threshold is at 100% (used in UI labels only)
  
  // Recommendations
  const recommendations = isDanger 
    ? [
        'Add more collateral immediately to protect your position',
        'Consider closing or reducing LOCs to lower risk',
        'Your position may be liquidated if prices move against you',
      ]
    : isCaution 
    ? [
        'Consider adding more collateral for safety',
        'Monitor your position closely',
        'Be prepared to add funds if risk increases',
      ]
    : [
        'Your collateral is healthy',
        'Continue monitoring market conditions',
      ]
  
  // Compact mode
  if (compact) {
    return (
      <div className={`p-4 rounded-lg border-2 ${riskBg}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-text-primary uppercase tracking-wide">
            {title}
          </span>
          <span className={`text-lg font-bold ${riskColor}`}>
            {ltv.toFixed(1)}%
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          {/* Background markers */}
          <div 
            className="absolute top-0 h-full bg-gray-300 dark:bg-gray-600" 
            style={{ left: `${cautionThreshold}%`, width: '1px' }}
          />
          <div 
            className="absolute top-0 h-full bg-gray-400 dark:bg-gray-500" 
            style={{ left: `${dangerThreshold}%`, width: '1px' }}
          />
          
          {/* Current LTV bar */}
          <div 
            className={`h-full ${barColor} transition-all duration-300`}
            style={{ width: `${Math.min(ltv, 100)}%` }}
          />
        </div>
        
        {/* Risk label */}
        <div className="flex items-center justify-between mt-2">
          <span className={`text-xs font-semibold ${riskColor}`}>
            {riskLevel}
          </span>
          <span className="text-xs text-text-secondary">
            Liquidation at 100%
          </span>
        </div>
      </div>
    )
  }
  
  // Full mode
  return (
    <div className={`p-6 rounded-xl border-2 ${riskBg}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-display font-black uppercase tracking-wide text-text-primary">
          {title}
        </h3>
        
        {isDanger ? (
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
        ) : isCaution ? (
          <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
        ) : (
          <ShieldCheckIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
        )}
      </div>
      
      {/* Current LTV Display */}
      <div className="mb-4">
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-4xl font-display font-black ${riskColor}`}>
            {ltv.toFixed(1)}%
          </span>
          <span className="text-sm text-text-secondary">LTV</span>
        </div>
        <p className={`text-sm font-semibold ${riskColor}`}>
          {riskLevel}
        </p>
      </div>
      
      {/* Visual Progress Bar */}
      <div className="mb-4">
        <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-2">
          {/* Background zones */}
          <div className="absolute inset-0 flex">
            <div className="flex-1 bg-green-100 dark:bg-green-900/30" style={{ maxWidth: `${cautionThreshold}%` }} />
            <div className="flex-1 bg-yellow-100 dark:bg-yellow-900/30" style={{ maxWidth: `${dangerThreshold - cautionThreshold}%` }} />
            <div className="flex-1 bg-red-100 dark:bg-red-900/30" />
          </div>
          
          {/* Threshold markers */}
          <div 
            className="absolute top-0 h-full w-0.5 bg-gray-400 dark:bg-gray-500" 
            style={{ left: `${cautionThreshold}%` }}
          />
          <div 
            className="absolute top-0 h-full w-0.5 bg-gray-500 dark:bg-gray-400" 
            style={{ left: `${dangerThreshold}%` }}
          />
          
          {/* Current LTV indicator */}
          <div 
            className={`absolute top-0 h-full ${barColor} transition-all duration-500 ease-out`}
            style={{ width: `${Math.min(ltv, 100)}%` }}
          />
          
          {/* Current position marker */}
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-1 h-6 bg-white dark:bg-gray-900 shadow-lg transition-all duration-500 ease-out"
            style={{ left: `${Math.min(ltv, 100)}%` }}
          />
        </div>
        
        {/* Legend */}
        <div className="flex justify-between text-xs text-text-secondary">
          <span>0% (Safe)</span>
          <span>50%</span>
          <span>75%</span>
          <span>100% (Liquidation)</span>
        </div>
      </div>
      
      {/* Collateral Details */}
      <div className="space-y-2 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Total Collateral:</span>
          <span className="font-semibold text-text-primary">${collateralValueUSD.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Active LOCs Value:</span>
          <span className="font-semibold text-text-primary">${locValueUSD.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-text-secondary">Available Credit:</span>
          <span className="font-semibold text-green-600 dark:text-green-400">
            ${(collateralValueUSD * 0.75 - locValueUSD).toFixed(2)}
          </span>
        </div>
      </div>
      
      {/* Recommendations */}
      {showRecommendations && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
            {isDanger ? '⚠️ Urgent Actions Required' : isCaution ? '💡 Recommendations' : '✅ You\'re Safe'}
          </h4>
          
          <ul className="space-y-2">
            {recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-text-secondary">
                <span className={`mt-0.5 ${isDanger ? 'text-red-600' : isCaution ? 'text-yellow-600' : 'text-green-600'}`}>
                  {isDanger ? '•' : isCaution ? '•' : '✓'}
                </span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
          
          {/* Action buttons for at-risk positions */}
          {(isDanger || isCaution) && (
            <div className="flex gap-2 mt-4">
              <a 
                href="/vaults"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-orange hover:bg-brand-orange/90 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                <ArrowUpIcon className="w-4 h-4" />
                Add Collateral
              </a>
              <a 
                href="/locs"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg transition-colors text-sm"
              >
                Manage LOCs
              </a>
            </div>
          )}
        </div>
      )}
      
      {/* Educational note */}
      {!isDanger && !isCaution && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-text-secondary">
            <strong className="text-text-primary">Pro Tip:</strong> Maintaining your LTV below 50% gives you the best protection against market volatility and liquidation risk.
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Compact version for quick overview
 */
export function LiquidationRiskBadge({
  collateralValueUSD,
  locValueUSD,
}: Pick<LiquidationRiskIndicatorProps, 'collateralValueUSD' | 'locValueUSD'>) {
  const ltv = collateralValueUSD > 0 ? (locValueUSD / collateralValueUSD) * 100 : 0
  
  const isDanger = ltv > 75
  const isCaution = ltv > 50 && ltv <= 75
  
  const badgeColor = isDanger 
    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700' 
    : isCaution 
    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700' 
    : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700'
  
  const icon = isDanger ? '⚠️' : isCaution ? '⚡' : '✓'
  
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${badgeColor} text-sm font-semibold`}>
      <span>{icon}</span>
      <span>LTV: {ltv.toFixed(1)}%</span>
    </div>
  )
}

